import {
    ASSIGN_RULES_TO_ENTITY,
    GET_UNASSIGNED_RULES,
    GET_ACTIVE_PROJECT_COMPLIANCE_RULES,
    get_headers,
    get_project_id
} from '@/utils/constants/constants.js';
import alert_mixin from '@/utils/mixins/alert_mixin';
import axios from "axios";
import 'datatables.net';
import 'datatables.net-dt/css/dataTables.dataTables.css';
import $ from 'jquery';

export default {
    name: 'AssignComplianceRules',
    mixins: [alert_mixin],
    data: () => ({
        headers: get_headers(),
        project_id: get_project_id(),
        entity_type_id: sessionStorage.getItem("entity_type_id"),
        selected_entity_type: 'all',
        assigned_rules: [],
        unassigned_rules: [],
        entity_type_name: '',
        table_loading: false,
        show_archived: false
    }),
    computed: {
        filtered_rules() {
            if (this.selected_entity_type === 'all') {
                return this.unassigned_rules;
            }
            return this.unassigned_rules.filter(rule => 
                rule.applicable_entities.includes(this.selected_entity_type)
            );
        }
    },
    watch: {
        filtered_rules() {
            this.$nextTick(() => {
                this.init_data_table();
            });
        }
    },
    mounted() {
        this.get_unassiged_rules();
    },
    methods: {
        init_data_table() {
            if (this.dataTable) {
                this.dataTable.destroy();
            }
            
            this.dataTable = $('#rules_table').DataTable({
                responsive: true,
                emptyTable: "No data available in table",
                language: {
                    info: "",
                    lengthMenu: "_MENU_&nbsp;&nbsp;Entries per page"
                }
            });
        },
        
        get_assigned_rules() {
            this.table_loading = true;
            const request_data = {
                project_id: this.project_id,
                entity_type_id: this.entity_type_id,
                include_archived_rules: this.show_archived
            };

            axios.post(
                GET_ACTIVE_PROJECT_COMPLIANCE_RULES,
                request_data,
                { headers: this.headers }
            )
            .then(response => {
                if (response.data && response.data.data) {
                    const rules_data = response.data.data.rules || [];
                    
                    this.assigned_rules = rules_data.map(rule => ({
                        id: rule.id,
                        name: rule.name || 'Unnamed Rule',
                        description: rule.description || 'No description available',
                        frequency: rule.frequency || 'N/A',
                        is_active: rule.is_active || false,
                        data_point: rule.data_point?.data_point_name || 'N/A',
                        created_at: rule.created_at ? new Date(rule.created_at).toLocaleDateString() : 'N/A',
                        _rawData: rule
                    }));
                } else {
                    this.assigned_rules = [];
                }
            })
            .catch(error => {
                this.assigned_rules = [];
                this.show_error("Error loading assigned rules: " + (error.response?.data?.message || error.message));
            })
            .finally(() => {
                this.table_loading = false;
            });
        },

        get_unassiged_rules() {
            this.table_loading = true;
            const request_data = {
                project_id: this.project_id,
                entity_type_id: this.entity_type_id,
                include_archived: this.show_archived
            };

            axios.post(
                GET_UNASSIGNED_RULES,
                request_data,
                { headers: this.headers }
            )
            .then(response => {
                if (response.data && response.data.data) {
                    const response_data = response.data.data;
                    this.entity_type_name = response_data.entity_type?.name || 'Unknown Entity Type';
                    
                    const rules_data = response_data.unassigned_rules || [];
                    
                    this.unassigned_rules = rules_data.map(rule => ({
                        id: rule.id,
                        name: rule.name || 'Unnamed Rule',
                        description: rule.description || 'N/A',
                        frequency: rule.frequency || 'N/A',
                        is_active: rule.is_active || false,
                        data_point: rule.data_point?.data_point_name || 'N/A',
                        created_at: rule.created_at ? new Date(rule.created_at).toLocaleDateString() : 'N/A',
                        _rawData: rule
                    }));
                } else {
                    this.unassigned_rules = [];
                    this.entity_type_name = '';
                }
            })
            .catch(error => {
                this.unassigned_rules = [];
                this.entity_type_name = 'Error loading entity type';
                this.show_error("Error loading compliance rules: " + (error.response?.data?.message || error.message));
            })
            .finally(() => {
                this.table_loading = false;
            });
        },

        toggle_rule_import(rule) {
            this.table_loading = true;
            const is_assigned = this.is_rule_imported(rule.id);
            const request_data = {
                project_id: this.project_id,
                entity_type_id: this.entity_type_id,
                compliance_rule_id: rule.id
            };

            const endpoint = is_assigned 
                ? 'UNASSIGN_RULES_FROM_ENTITY' 
                : ASSIGN_RULES_TO_ENTITY;

            axios.post(
                endpoint,
                request_data,
                { headers: this.headers }
            )
            .then(response => {
                if (response.data && response.data.success !== false) {
                    // Update the assigned rules list
                    if (is_assigned) {
                        this.assigned_rules = this.assigned_rules.filter(r => r.id !== rule.id);
                    } else {
                        this.assigned_rules.push(rule);
                    }
                    
                    // Update the unassigned rules list if needed
                    if (is_assigned) {
                        this.unassigned_rules.push(rule);
                    } else {
                        this.unassigned_rules = this.unassigned_rules.filter(r => r.id !== rule.id);
                    }
                    this.show_success(`Rule ${is_assigned ? 'unassigned' : 'assigned'} successfully!`);
                } else {
                    this.show_error(response.data?.message || `Failed to ${is_assigned ? 'unassign' : 'assign'} rule`);
                }
            })
            .catch(error => {
                this.show_error("Error toggling rule assignment: " + (error.response?.data?.message || error.message));
            })
            .finally(() => {
                this.table_loading = false;
            });
        },

        is_rule_imported(ruleId) {
            return this.assigned_rules.some(rule => rule.id === ruleId);
        }
    },
    beforeDestroy() {
        if (this.dataTable) {
            this.dataTable.destroy();
        }
    }
}